import { isAfter, isBefore, isSameDay } from 'date-fns';
import { isNaN, toString } from 'lodash-es';
import * as yup from 'yup';
import PostViewerSelectorSchema from '../PostViewerSelector/postViewerSelector.validator';

const schema = yup
  .object()
  .shape({
    name: yup
      .string()
      .required('You must enter a name')
      .max(110, 'Name is too long'),
    description: yup
      .string()
      .required('You must enter a description')
      .max(2048, 'Description must be less than 2048 characters'),
    startsOn: yup
      .date()
      .required('You must enter a start date')
      .typeError('You must enter a valid date'),
    endsOn: yup
      .date()
      .required('You must enter an end date')
      .typeError('You must enter a valid date')
      .when(['startsOn'], (startsOn, schemaRef) =>
        schemaRef.test({
          name: 'endsOn',
          message: 'End date must be after start date',
          test: (endsOn) =>
            isSameDay(startsOn, endsOn) || isAfter(endsOn, startsOn),
        }),
      ),
    applyDeadline: yup
      .date()
      .notRequired()
      .nullable()
      .typeError('You must enter a valid date')
      .when(['endsOn'], (endsOn, schemaRef) =>
        schemaRef.test({
          name: 'applyDeadline',
          message: 'Application deadline must be before end date',
          test: (applyDeadline) =>
            !applyDeadline || isBefore(applyDeadline, endsOn),
        }),
      ),
    startsAt: yup.date().when('isOneTime', {
      is: true,
      then: yup
        .date()
        .required('You must enter a start time')
        .typeError('You must enter a valid start time'),
      otherwise: yup.date().notRequired().nullable(),
    }),
    endsAt: yup
      .date()
      .when(['isOneTime', 'startsAt'], (isOneTime, startsAt, schemaRef) => {
        if (isOneTime) {
          return schemaRef.typeError('You must enter a valid end time').test({
            name: 'endsAt',
            message: 'End time must be after start time',
            test: (endsAt) => !isOneTime || isAfter(endsAt, startsAt),
          });
        }
        return schemaRef.notRequired().nullable();
      }),
    timeCommitmentMin: yup
      .number()
      .when('isOneTime', (isOneTime, schemaRef) => {
        if (isOneTime) {
          return schemaRef
            .transform((value) => (isNaN(value) ? undefined : value))
            .nullable()
            .notRequired();
        }
        return schemaRef
          .required('You must enter a minimum time commitment')
          .integer('Minimum time commitment must be a whole number')
          .typeError('Minimum time commitment must be at least 1 hour')
          .min(1, 'Minimum time commitment must be at least 1 hour');
      }),
    timeCommitmentMax: yup
      .number()
      .when(
        ['isOneTime', 'timeCommitmentMin'],
        (isOneTime, timeCommitmentMin, schemaRef) => {
          if (isOneTime) {
            return schemaRef
              .transform((value) => (isNaN(value) ? undefined : value))
              .nullable()
              .notRequired();
          }
          return schemaRef
            .required('You must enter a maximum time commitment')
            .integer('Maximum time commitment must be a whole number')
            .typeError('Maximum time commitment must be at least 1 hour')
            .min(
              timeCommitmentMin,
              'Must be greater than minimum time commitment',
            )
            .max(365 * 24, 'That value seems too high');
        },
      ),
    location: yup
      .string()
      .required('You must enter a location')
      .max(255, 'Location must be less than 255 characters'),
    additionalDetails: yup
      .string()
      .notRequired()
      .nullable()
      .max(255, 'Additional details must be less than 255 characters'),
    volunteersReq: yup
      .number()
      .required('You must enter number of volunteers required')
      .integer('Number of volunteers required must be a whole number')
      .typeError('Number of volunteers required must be at least 1')
      .min(1, 'Number of volunteers required must be at least 1'),
    estimatedBillRate: yup
      .number()
      .required('You must enter an estimated bill rate')
      .integer('Estimated bill rate must be a whole number')
      .typeError('Estimated bill rate must be at least 1')
      .min(1, 'Estimated bill rate must be at least 1'),
    mandatorySkills: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.number().required(),
        }),
      )
      .required('You must select at least one mandatory skill')
      .min(1, 'You must select at least one mandatory skill'),
  })
  .concat(PostViewerSelectorSchema);

const defaultFormValues = (data) => ({
  id: data?.id || null,
  postAs: data?.postAs || 'org',
  targetViewers: data?.targetViewers || 'all',
  targetOrgs: data?.targetOrgs || [],
  isFeatured: data?.isFeatured || false,
  name: data?.name || '',
  description: data?.description || '',
  startsOn: data ? new Date(data.startsOn) : new Date(),
  endsOn: data ? new Date(data.endsOn) : new Date(),
  // applyDeadline: data?.applyDeadline ? new Date(data.applyDeadline) : null,
  applyDeadline: null,
  type: data?.type || '',
  isOneTime: data?.type === 'one-time-event',
  startsAt: data?.startsAt ? new Date(data.startsAt) : new Date(),
  endsAt: data?.endsAt ? new Date(data.endsAt) : new Date(),
  timeCommitmentMin: data ? toString(data.timeCommitmentMin) : '',
  timeCommitmentMax: data ? toString(data.timeCommitmentMax) : '',
  virtual: data ? data.virtual : false,
  location: data?.location || '',
  additionalDetails: data?.additionalDetails || '',
  volunteersReq: data ? toString(data.volunteersReq) : '',
  estimatedBillRate: data ? toString(data.estimatedBillRate) : '',
  mandatorySkills: data?.mandatorySkills || [],
  optionalSkills: data?.optionalSkills || [],
  causes: data?.causes || [],
});

export { schema, defaultFormValues };
